import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Checkbox,
  FormGroup,
  Paper,
  Chip,
  Divider,
  Alert,
  Collapse,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import SpeechButton from '../../common/SpeechButton';
import LearningMaterialContent from '../../LearningMaterial/LearningMaterialContent';
import { htmlToPlainText } from '../../Exercise/materialConfigUtils';
import { LearningMaterial } from '../../../services/learningMaterialService';
import QuestionProgressControls from './QuestionProgressControls';

interface MultichoiceOption {
  id: string;
  text: string;
  isCorrect: boolean;
}

interface MultichoiceQuestion {
  id: string;
  question: string;
  options: MultichoiceOption[];
  explanation?: string;
}

interface MultichoiceAnswer {
  questionId: string;
  selectedOptionIds: string[];
}

interface QuestionStudentProps {
  question: MultichoiceQuestion;
  questionIndex: number;
  selectedOptionIds: string[];
  onAnswerChange: (questionId: string, selectedOptionIds: string[]) => void;
  feedback?: {
    isCorrect: boolean;
    correctAnswer: string;
    userAnswer: string;
  };
  disabled: boolean;
  allowMultipleSelection: boolean;
  voiceCode?: string;
}

const QuestionStudent: React.FC<QuestionStudentProps> = ({
  question,
  questionIndex,
  selectedOptionIds,
  onAnswerChange,
  feedback,
  disabled,
  allowMultipleSelection,
  voiceCode,
}) => {
  // Shuffle options once per question, keeping order stable during session
  const shuffledOptions = useMemo(() => {
    const options = [...question.options];
    // Fisher-Yates shuffle with seeded randomness based on question id
    for (let i = options.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [options[i], options[j]] = [options[j], options[i]];
    }
    return options;
  }, [question.id]);

  const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedId = event.target.value;
    onAnswerChange(question.id, [selectedId]);
  };

  const handleCheckboxChange = (optionId: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    let newSelection: string[];
    
    if (isChecked) {
      newSelection = [...selectedOptionIds, optionId];
    } else {
      newSelection = selectedOptionIds.filter(id => id !== optionId);
    }
    
    onAnswerChange(question.id, newSelection);
  };

  const getFeedbackIcon = () => {
    if (!feedback) return null;
    return feedback.isCorrect ? (
      <CheckCircleIcon sx={{ color: 'success.main', ml: 1 }} />
    ) : (
      <CancelIcon sx={{ color: 'error.main', ml: 1 }} />
    );
  };

  const isOptionCorrect = (optionId: string) => {
    return question.options.find(opt => opt.id === optionId)?.isCorrect || false;
  };

  const getOptionColor = (optionId: string) => {
    if (!feedback || !disabled) return undefined;
    
    const isCorrect = isOptionCorrect(optionId);
    const isSelected = selectedOptionIds.includes(optionId);
    
    if (isCorrect) {
      return 'success.light';
    } else if (isSelected && !isCorrect) {
      return 'error.light';
    }
    
    return undefined;
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Box display="flex" alignItems="flex-start" mb={2}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {questionIndex + 1}. {question.question}
        </Typography>
        <Box display="flex" alignItems="center">
          <SpeechButton
            id={`question_${question.id}`}
            text={question.question}
            voiceCode={voiceCode || 'fi-FI-NooraNeural'}
          />
          {getFeedbackIcon()}
        </Box>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {allowMultipleSelection ? (
        <FormGroup>
          {shuffledOptions.map((option) => (
            <FormControlLabel
              key={option.id}
              control={
                <Checkbox
                  checked={selectedOptionIds.includes(option.id)}
                  onChange={handleCheckboxChange(option.id)}
                  disabled={disabled}
                />
              }
              label={
                <Box display="flex" alignItems="center" sx={{ width: '100%' }}>
                  <Typography sx={{ flexGrow: 1 }}>{option.text}</Typography>
                  <SpeechButton
                    id={`option_${question.id}_${option.id}`}
                    text={option.text}
                    voiceCode={voiceCode || 'fi-FI-NooraNeural'}
                  />
                </Box>
              }
              sx={{
                p: 1,
                m: 0.5,
                borderRadius: 1,
                backgroundColor: getOptionColor(option.id),
                '&:hover': {
                  backgroundColor: disabled ? getOptionColor(option.id) : 'action.hover',
                },
              }}
            />
          ))}
        </FormGroup>
      ) : (
        <FormControl component="fieldset">
          <RadioGroup
            value={selectedOptionIds[0] || ''}
            onChange={handleRadioChange}
          >
            {shuffledOptions.map((option) => (
              <FormControlLabel
                key={option.id}
                value={option.id}
                control={<Radio disabled={disabled} />}
                label={
                  <Box display="flex" alignItems="center" sx={{ width: '100%' }}>
                    <Typography sx={{ flexGrow: 1 }}>{option.text}</Typography>
                    <SpeechButton
                      id={`option_${question.id}_${option.id}`}
                      text={option.text}
                      voiceCode={voiceCode || 'fi-FI-NooraNeural'}
                    />
                  </Box>
                }
                sx={{
                  p: 1,
                  m: 0.5,
                  borderRadius: 1,
                  backgroundColor: getOptionColor(option.id),
                  '&:hover': {
                    backgroundColor: disabled ? getOptionColor(option.id) : 'action.hover',
                  },
                }}
              />
            ))}
          </RadioGroup>
        </FormControl>
      )}

      {/* Show feedback */}
      {feedback && disabled && (
        <Collapse in={true}>
          <Box mt={3}>
            {/* Result Summary */}
            <Alert 
              severity={feedback.isCorrect ? "success" : "error"}
              sx={{ mb: 2 }}
              icon={feedback.isCorrect ? <CheckCircleIcon /> : <CancelIcon />}
            >
              <Typography variant="body1" fontWeight="medium">
                {feedback.isCorrect ? "Oikein!" : "Väärin"}
              </Typography>
            </Alert>

            {/* Answer Details */}
            {!feedback.isCorrect && (
              <Paper elevation={0} sx={{ p: 2, mb: 2, backgroundColor: 'grey.50', borderLeft: '4px solid', borderColor: 'info.main' }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Sinun vastauksesi:</strong> {feedback.userAnswer || "Ei vastausta"}
                </Typography>
                <Typography variant="body2" color="success.dark">
                  <strong>Oikea vastaus:</strong> {feedback.correctAnswer}
                </Typography>
              </Paper>
            )}

            {/* Explanation Box - Always show if available */}
            {question.explanation && (
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 2, 
                  backgroundColor: 'info.light', 
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'info.main'
                }}
              >
                <Box display="flex" alignItems="flex-start" gap={1}>
                  <LightbulbIcon sx={{ color: 'info.dark', mt: 0.3 }} />
                  <Box>
                    <Typography variant="subtitle2" color="info.dark" fontWeight="bold" gutterBottom>
                      Selitys
                    </Typography>
                    <Typography variant="body2" color="info.contrastText">
                      {question.explanation}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            )}
          </Box>
        </Collapse>
      )}

      {/* Show selection type */}
      <Box mt={2}>
        <Chip 
          size="small" 
          label={allowMultipleSelection ? "Voit valita useita vaihtoehtoja" : "Valitse yksi vaihtoehto"} 
          variant="outlined"
        />
      </Box>
    </Paper>
  );
};

interface MaterialBasedMultichoiceStudentProps {
  config: {
    customPrompt: string;
    questions: MultichoiceQuestion[];
    allowMultipleSelection: boolean;
    voiceCode: string;
    showMaterialToStudent?: boolean;
  };
  linkedMaterial?: {
    id: number;
    title: string;
    content: string;
    sections?: LearningMaterial['sections'];
    materialMedia?: LearningMaterial['materialMedia'];
    showToStudent?: boolean;
  } | null;
  initialAnswers: MultichoiceAnswer[];
  onAnswerChange: (questionId: string, selectedOptionIds: string[]) => void;
  disabled: boolean;
  assignmentStatus: 'New' | 'Active' | 'InReview';
  hasSubmitted: boolean;
  feedback?: {
    isCorrect: boolean;
    correctAnswer: string;
    userAnswer: string;
  }[];
}

const MaterialBasedMultichoiceStudent: React.FC<MaterialBasedMultichoiceStudentProps> = ({
  config,
  linkedMaterial,
  initialAnswers,
  onAnswerChange,
  disabled,
  assignmentStatus,
  hasSubmitted,
  feedback,
}) => {
  const [answers, setAnswers] = useState<{ [questionId: string]: string[] }>({});
  const [questionViewMode, setQuestionViewMode] = useState<'all' | 'single'>('all');
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const shouldShowMaterial = linkedMaterial?.showToStudent ?? config.showMaterialToStudent;
  const materialPlainText = linkedMaterial ? htmlToPlainText(linkedMaterial.content) : '';

  useEffect(() => {
    // Initialize answers from props
    const initialAnswerMap: { [questionId: string]: string[] } = {};
    initialAnswers.forEach(answer => {
      initialAnswerMap[answer.questionId] = answer.selectedOptionIds;
    });
    setAnswers(initialAnswerMap);
  }, [initialAnswers]);

  useEffect(() => {
    setCurrentQuestionIndex((previousIndex) => {
      if (config.questions.length === 0) {
        return 0;
      }

      return Math.min(previousIndex, config.questions.length - 1);
    });
  }, [config.questions.length]);

  const answeredFlags = useMemo(
    () => config.questions.map((question) => (answers[question.id] || []).length > 0),
    [answers, config.questions],
  );

  const visibleQuestions = questionViewMode === 'single'
    ? config.questions.slice(currentQuestionIndex, currentQuestionIndex + 1).map((question, index) => ({
      question,
      index: currentQuestionIndex + index,
    }))
    : config.questions.map((question, index) => ({ question, index }));

  const handleAnswerChange = (questionId: string, selectedOptionIds: string[]) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: selectedOptionIds,
    }));
    onAnswerChange(questionId, selectedOptionIds);
  };

  return (
    <Box>
      {/* Display Material - only if showMaterialToStudent is true */}
      {shouldShowMaterial && linkedMaterial && (
        <Paper elevation={2} sx={{ p: 3, mb: 3, backgroundColor: 'grey.50' }}>
          <Typography variant="h6" gutterBottom>
            {linkedMaterial.title}
          </Typography>
          <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2 }}>
            <LearningMaterialContent
              material={linkedMaterial as any}
              defaultView="sectioned"
              allowViewToggle={true}
              allowHide={true}
            />
          </Box>
          <Box mt={2}>
            <SpeechButton
              id="material-speech"
              text={materialPlainText}
              voiceCode={config.voiceCode || 'fi-FI-NooraNeural'}
            />
          </Box>
        </Paper>
      )}

      {/* Questions */}
      <Box>
        <Typography variant="h5" gutterBottom>
          Vastaa kysymyksiin materiaalin perusteella
        </Typography>

        <QuestionProgressControls
          totalQuestions={config.questions.length}
          answeredFlags={answeredFlags}
          currentQuestionIndex={currentQuestionIndex}
          viewMode={questionViewMode}
          onViewModeChange={setQuestionViewMode}
          onPrevious={() => setCurrentQuestionIndex((currentValue) => Math.max(0, currentValue - 1))}
          onNext={() => setCurrentQuestionIndex((currentValue) => Math.min(config.questions.length - 1, currentValue + 1))}
        />
        
        {config.customPrompt && (
          <Paper elevation={1} sx={{ p: 2, mb: 3, backgroundColor: 'info.light' }}>
            <Typography variant="body2" color="info.contrastText">
              <strong>Lisäohje:</strong> {config.customPrompt}
            </Typography>
          </Paper>
        )}

        {visibleQuestions.map(({ question, index }) => (
          <QuestionStudent
            key={question.id}
            question={question}
            questionIndex={index}
            selectedOptionIds={answers[question.id] || []}
            onAnswerChange={handleAnswerChange}
            feedback={feedback ? feedback[index] : undefined}
            disabled={disabled || hasSubmitted}
            allowMultipleSelection={config.allowMultipleSelection}
            voiceCode={config.voiceCode}
          />
        ))}
      </Box>
    </Box>
  );
};

export default MaterialBasedMultichoiceStudent;